import React, { useState, useEffect } from 'react';
import './HoursWorkedModal.css';
import closeIcon from './../../assets/close-icon.png';
import warningIcon from './../../assets/warning-icon.png';
import greyWarningIcon from './../../assets/grey-warning-icon.png';
import HoursWorkedTooltip from './HoursWorkedTooltip';

// Modal for capturing employee/owner hours worked with two-step workflow
const HoursWorkedModal = ({
  show,
  onClose,
  onSave,
  onCalculateGuidantHours,
  onUpdateTermination,
  employeeFullName,
  employeeKey,
  employeeData,
  censusYearData
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [dateError, setDateError] = useState('');

  // Determine if person is owner based on ownership percentage
  const ownershipPercent = employeeData?.OwnershipPercent?.FinalValue?.Value;
  const isOwner = ownershipPercent && parseFloat(ownershipPercent) > 0;
  const personType = isOwner ? 'Owner' : 'Employee';

  // Show warning if owner hours are below 500 (bona fide employee requirement)
  const hoursValue = parseFloat(inputValue) || 0;
  const showOwnerWarning = isOwner && selectedOption === 'manual_entry' && hoursValue > 0 && hoursValue < 500;

  const validateDate = (dateString) => {
    if (!dateString) return true;
    const date = new Date(dateString);
    const year = date.getFullYear();
    return year >= 1900 && year <= 2100;
  };

  // Extract date from leave of absence note format
  const extractDateFromLeaveNote = (note) => {
    const match = note.match(/On Leave of Absence beginning (\d{2}\/\d{2}\/\d{4})\./);
    if (match) {
      const [month, day, year] = match[1].split('/');
      const result = `${year}-${month}-${day}`;
      return result;
    }
    return '';
  };

  // Detect previous selection to pre-populate modal
  const detectPreviousSelection = () => {
    const hours = employeeData?.HoursWorked?.FinalValue?.Value;
    const note = employeeData?.HoursWorkedNote?.FinalValue?.Value;
    const source = employeeData?.HoursWorked?.FinalValue?.Source;
    const terminationDate = employeeData?.DateOfTermination?.FinalValue?.Value;
    
    const hoursNum = parseFloat(hours) || 0;

    if (source === 'guidant_estimate') {
      return { step: 1, option: 'guidant_estimate', value: '' };
    }

    if (hoursNum > 0 && (!note || note.trim() === '')) {
      return { step: 1, option: 'manual_entry', value: hours };
    }

    if (hoursNum === 0 && note && note.trim() !== '') {
      if (note.startsWith('On Leave of Absence beginning')) {
        const dateValue = extractDateFromLeaveNote(note);
        return { step: 2, option: 'leave_absence', value: dateValue };
      }
      
      if (note === 'Active but did not work during plan year.') {
        return { step: 2, option: 'still_active', value: '' };
      }
      
      return { step: 2, option: 'other', value: note };
    }

    if (hoursNum === 0 && terminationDate) {
      const [month, day, year] = terminationDate.split('-');
      const formatted = `${year}-${month}-${day}`;
      return { step: 2, option: 'terminated', value: formatted };
    }

    return null;
  };

  // Pre-populate form when modal opens
  useEffect(() => {
    if (show) {
      const previousSelection = detectPreviousSelection();
      
      if (previousSelection) {
        setCurrentStep(previousSelection.step);
        setSelectedOption(previousSelection.option);
        setInputValue(previousSelection.value);
      } else {
        setCurrentStep(1);
        setSelectedOption('');
        setInputValue('');
      }
    }
  }, [show]);

  if (!show) {
    return null;
  }

  // Require hire date before proceeding
  const hireDateExists = employeeData?.DateOfHire?.FinalValue?.Value;
  if (!hireDateExists) {
    return (
      <div className="hwm-modal-overlay" onClick={onClose}>
        <div className="hwm-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="hwm-modal-header">
            <div className="hwm-header-title">
              <img src={warningIcon} alt="Warning" className="hwm-header-icon" />
              <h2>Hours Worked — {employeeFullName}</h2>
            </div>
            <img
              src={closeIcon}
              alt="Close"
              className="hwm-modal-close-icon"
              onClick={onClose}
            />
          </div>
          <div className="hwm-error-banner">
            <span>Please ensure Hire Date is valid before proceeding.</span>
          </div>
          <div className="hwm-modal-body">
            <button className="hwm-single-button" onClick={onClose}>
              CLOSE
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleContinueStep1 = () => {
    if (selectedOption === 'manual_entry') {
      if (!inputValue || inputValue === '0') {
        alert('Please enter hours greater than 0');
        return;
      }
      onSave(employeeKey, inputValue, '');
      onClose();
    } else if (selectedOption === 'guidant_estimate') {
      onCalculateGuidantHours(employeeKey);
      onClose();
    } else if (selectedOption === 'no_work') {
      setCurrentStep(2);
    }
  };

  const handleContinueStep2 = () => {
  setDateError('');

  if (selectedOption === 'terminated') {
    if (!inputValue) {
      alert('Please select a termination date');
      return;
    }
    if (!validateDate(inputValue)) {
      setDateError('Invalid date. Year must be between 1900 and 2100.');
      return;
    }
    
    onUpdateTermination(employeeKey, inputValue);
    onSave(employeeKey, '0', '');
  } else {
    // Clear termination date for non-terminated options
    onUpdateTermination(employeeKey, '');
    
    if (selectedOption === 'leave_absence') {
      if (!inputValue) {
        alert('Please select a leave start date');
        return;
      }
      if (!validateDate(inputValue)) {
        setDateError('Invalid date. Year must be between 1900 and 2100.');
        return;
      }
      
      // FIX: Simple string manipulation instead of Date object
      const [year, month, day] = inputValue.split('-');
      const formattedDate = `${month}/${day}/${year}`;
      
      onSave(employeeKey, '0', `On Leave of Absence beginning ${formattedDate}.`);
    } else if (selectedOption === 'still_active') {
      onSave(employeeKey, '0', 'Active but did not work during plan year.');
    } else if (selectedOption === 'other') {
      if (!inputValue.trim()) {
        alert('Please provide an explanation');
        return;
      }
      onSave(employeeKey, '0', inputValue);
    }
  }
  onClose();
};

  const handleBack = () => {
    setCurrentStep(1);
    setSelectedOption('');
    setInputValue('');
  };

  const handleIconMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setTooltipPosition({ x: rect.right + 10, y: rect.top });
    setShowTooltip(true);
  };

  const handleIconMouseLeave = () => {
    setShowTooltip(false);
  };

  const renderStep1 = () => (
    <>
      <div className="hwm-banner">
        {isOwner 
          ? 'OWNERS SHOULD WORK AT LEAST 500 HOURS/YEAR TO SATISFY THE BONA FIDE EMPLOYEE RULE.'
          : 'PLEASE REVIEW. EMPLOYEE HOURS ARE USED TO DETERMINE 401(K) PLAN ELIGIBILITY.'
        }
      </div>
      <div className="hwm-modal-body">
        <div className="hwm-content-wrapper">
          <h3 className="hwm-question">Did this {personType} work during the plan year?</h3>
          
          <div className="hwm-radio-group">
            <div className="hwm-radio-row">
              <input
                type="radio"
                id="manual_entry"
                name="work_status"
                value="manual_entry"
                checked={selectedOption === 'manual_entry'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="hwm-radio-input"
              />
              <label htmlFor="manual_entry" className="hwm-radio-label">
                <span className="hwm-radio-text">Yes, this {personType} worked — I'll enter hours</span>
              </label>
            </div>
            
            {selectedOption === 'manual_entry' && (
              <div className="hwm-nested-section">
                <label className="hwm-field-label">Estimated hours worked</label>
                <div className="hwm-input-with-icon">
                  <input
                    type="text"
                    className={`hwm-text-input hwm-has-icon ${showOwnerWarning ? 'hwm-error' : ''}`}
                    placeholder="Example: 500"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  {showOwnerWarning && (
                    <img
                      src={greyWarningIcon}
                      alt="Warning"
                      className="hwm-input-info-icon"
                      onMouseEnter={handleIconMouseEnter}
                      onMouseLeave={handleIconMouseLeave}
                    />
                  )}
                </div>
                {showOwnerWarning ? (
                  <p className="hwm-error-text">
                    To meet the Bona Fide Employee requirement, work at least 500 hours/year, paid or unpaid.
                  </p>
                ) : (
                  <p className="hwm-help-text">
                    Use your best estimate based on their role. Required for eligibility.
                  </p>
                )}
              </div>
            )}

            <div className="hwm-radio-row">
              <input
                type="radio"
                id="guidant_estimate"
                name="work_status"
                value="guidant_estimate"
                checked={selectedOption === 'guidant_estimate'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="hwm-radio-input"
              />
              <label htmlFor="guidant_estimate" className="hwm-radio-label">
                <span className="hwm-radio-text">Yes, this {personType} worked — I don't track hours</span>
                <span className="hwm-radio-subtext">Let Guidant provide an estimate based on their records.</span>
              </label>
            </div>

            <div className="hwm-radio-row">
              <input
                type="radio"
                id="no_work"
                name="work_status"
                value="no_work"
                checked={selectedOption === 'no_work'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="hwm-radio-input"
              />
              <label htmlFor="no_work" className="hwm-radio-label">
                <span className="hwm-radio-text">No, this {personType} didn't work</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="hwm-banner">
        {isOwner 
          ? 'OWNERS SHOULD WORK AT LEAST 500 HOURS/YEAR TO SATISFY THE BONA FIDE EMPLOYEE RULE.'
          : 'PLEASE REVIEW. EMPLOYEE HOURS ARE USED TO DETERMINE 401(K) PLAN ELIGIBILITY.'
        }
      </div>
      <div className="hwm-modal-body">
        <div className="hwm-content-wrapper">
          <h3 className="hwm-question">Why didn't this {personType} work?</h3>
          
          <div className="hwm-radio-group">
            <div className="hwm-radio-row">
              <input
                type="radio"
                id="terminated"
                name="no_work_reason"
                value="terminated"
                checked={selectedOption === 'terminated'}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  setInputValue('');
                  setDateError('');
                }}
                className="hwm-radio-input"
              />
              <label htmlFor="terminated" className="hwm-radio-label">
                <span className="hwm-radio-text">Terminated</span>
              </label>
            </div>
            
            {selectedOption === 'terminated' && (
              <div className="hwm-nested-section">
                <label className="hwm-field-label">Termination Date</label>
                <input
                  type="date"
                  className={`hwm-text-input hwm-date-input ${dateError && selectedOption === 'terminated' ? 'hwm-error' : ''}`}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setDateError('');
                  }}
                  placeholder="Select a date"
                />
                {dateError && selectedOption === 'terminated' && (
                  <p className="hwm-error-text">{dateError}</p>
                )}
              </div>
            )}

            <div className="hwm-radio-row">
              <input
                type="radio"
                id="leave_absence"
                name="no_work_reason"
                value="leave_absence"
                checked={selectedOption === 'leave_absence'}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  setInputValue('');
                  setDateError('');
                }}
                className="hwm-radio-input"
              />
              <label htmlFor="leave_absence" className="hwm-radio-label">
                <span className="hwm-radio-text">On leave of absence</span>
              </label>
            </div>
            
            {selectedOption === 'leave_absence' && (
              <div className="hwm-nested-section">
                <label className="hwm-field-label">Leave Start Date</label>
                <input
                  type="date"
                  className={`hwm-text-input hwm-date-input ${dateError && selectedOption === 'leave_absence' ? 'hwm-error' : ''}`}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setDateError('');
                  }}
                  placeholder="Select a date"
                />
                {dateError && selectedOption === 'leave_absence' && (
                  <p className="hwm-error-text">{dateError}</p>
                )}
              </div>
            )}

            <div className="hwm-radio-row">
              <input
                type="radio"
                id="still_active"
                name="no_work_reason"
                value="still_active"
                checked={selectedOption === 'still_active'}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  setInputValue('');
                }}
                className="hwm-radio-input"
              />
              <label htmlFor="still_active" className="hwm-radio-label">
                <span className="hwm-radio-text">Still active but didn't work</span>
              </label>
            </div>

            <div className="hwm-radio-row">
              <input
                type="radio"
                id="other"
                name="no_work_reason"
                value="other"
                checked={selectedOption === 'other'}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  setInputValue('');
                }}
                className="hwm-radio-input"
              />
              <label htmlFor="other" className="hwm-radio-label">
                <span className="hwm-radio-text">Other — Add an explanation</span>
              </label>
            </div>
            
            {selectedOption === 'other' && (
              <div className="hwm-nested-section">
                <label className="hwm-field-label">Type your explanation here</label>
                <textarea
                  className="hwm-textarea"
                  rows="4"
                  placeholder="Please provide a reason to meet compliance requirements."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const isStep1Valid = selectedOption && (selectedOption !== 'manual_entry' || inputValue);
  const isStep2Valid = selectedOption && 
    ((selectedOption === 'terminated' && inputValue) ||
     (selectedOption === 'leave_absence' && inputValue) ||
     selectedOption === 'still_active' ||
     (selectedOption === 'other' && inputValue.trim()));

  return (
    <div className="hwm-modal-overlay" onClick={onClose}>
      <div className="hwm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="hwm-modal-header">
          <div className="hwm-header-title">
            <img src={warningIcon} alt="Warning" className="hwm-header-icon" />
            <h2>Hours Worked — {employeeFullName}</h2>
          </div>
          <img
            src={closeIcon}
            alt="Close"
            className="hwm-modal-close-icon"
            onClick={onClose}
          />
        </div>
        
        {currentStep === 1 ? renderStep1() : renderStep2()}
        
        <div className="hwm-button-footer">
          {currentStep === 2 && (
            <button className="hwm-back-button" onClick={handleBack}>
              ← BACK
            </button>
          )}
          <button 
            className="hwm-continue-button"
            onClick={currentStep === 1 ? handleContinueStep1 : handleContinueStep2}
            disabled={currentStep === 1 ? !isStep1Valid : !isStep2Valid}
          >
            CONTINUE →
          </button>
        </div>
      </div>
      {showTooltip && (
        <HoursWorkedTooltip position={tooltipPosition} />
      )}
    </div>
  );
};

export default HoursWorkedModal;